import {Content, Portal, Root} from '@wandb/weave/components/Dialog/Dialog';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import clsx from 'clsx';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
  INPUT_ACTION,
  useRegisterAction,
  useRegisterScope,
} from '../../../inputSystem';
import {mediaFullscreenModalImageClickAndDrag} from '../../../services/analytics/productEvents';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import * as S from '../../ImageCard/ImageCard.styles';
import {type MediaSize} from '../../ImageCard/resolveMediaSize';
import {useSingleRunLink} from '../../MultiRunWorkspace/SingleRunMode';
import {getCurrentZoomNumber, zoomIn, zoomOut} from '../controls/ZoomControl';
import {useFullscreenScrollAnalytics} from '../hooks/useFullscreenScrollAnalytics';
import {
  type DragSummary,
  useScrollCenterPan,
} from '../hooks/useScrollCenterPan';
import {containFit} from '../sizing/containFit';
import {zoomedSize} from '../sizing/zoomedSize';
import {TileMediaLoader} from '../TileMediaLoader';
import {useMediaBrowserModalContext} from './MediaBrowserModalContext';
import {MediaBrowserModalFooter} from './MediaBrowserModalFooter';
import {MediaBrowserModalHeader} from './MediaBrowserModalHeader';
import {type MediaBrowserModalProps, ZoomSource} from './types';
import {useModalKeyboardHandling} from './useModalKeyboardHandling';

const MIN_CONTENT_SIZE = {width: 400, height: 300};

const useRegisterKeyboardShortcuts = () => {
  const {zoom, setZoom, zoomToFitPercentage} = useMediaBrowserModalContext();

  useRegisterScope('mediaBrowserModal');

  const onZoomIn = useCallback(() => {
    setZoom(zoomIn(zoom, zoomToFitPercentage), ZoomSource.Keyboard);
  }, [zoom, zoomToFitPercentage, setZoom]);
  useRegisterAction(INPUT_ACTION.MEDIA_MODAL_ZOOM_IN, onZoomIn);
  useRegisterAction(INPUT_ACTION.MEDIA_MODAL_ZOOM_IN_SHIFTED, onZoomIn);

  useRegisterAction(INPUT_ACTION.MEDIA_MODAL_ZOOM_OUT, () => {
    setZoom(zoomOut(zoom, zoomToFitPercentage), ZoomSource.Keyboard);
  });

  useRegisterAction(INPUT_ACTION.MEDIA_MODAL_RESET_ZOOM, () => {
    setZoom(100, ZoomSource.Keyboard);
  });

  useRegisterAction(INPUT_ACTION.MEDIA_MODAL_ZOOM_TO_FIT, () => {
    setZoom('fit', ZoomSource.Keyboard);
  });
};

export const MediaBrowserFullscreenModal: React.FC<MediaBrowserModalProps> = ({
  isOpen,
  modalData,
}) => {
  const {
    currentTileMediaSpec,
    closeModal,
    zoom,
    setZoomToFitPercentage,
    updateModalData,
  } = useMediaBrowserModalContext();
  const {handleModalKeyDown} = useModalKeyboardHandling();

  useRegisterKeyboardShortcuts();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [availableDimensions, setAvailableDimensions] =
    useState(MIN_CONTENT_SIZE);
  const [containerSize, setContainerSize] = useState(MIN_CONTENT_SIZE);

  // Fraction (0–100) of content where the viewport center should stay when zooming
  const [viewportCenterPercent, setViewportCenterPercent] = useState({
    x: 50,
    y: 50,
  });

  const calculateAvailableDimensions = useCallback(() => {
    if (!modalContentRef.current || !modalData) {
      return;
    }
    const {width: availableWidth, height: availableHeight} =
      modalContentRef.current.getBoundingClientRect();

    setContainerSize({width: availableWidth, height: availableHeight});

    const container = {
      width: Math.max(availableWidth, MIN_CONTENT_SIZE.width),
      height: Math.max(availableHeight, MIN_CONTENT_SIZE.height),
    };

    const {imageDimensions} = modalData;
    if (!imageDimensions) {
      const {width, height} = containFit(MIN_CONTENT_SIZE, container);
      setAvailableDimensions({width, height});
      return;
    }

    const fit = containFit(imageDimensions, container);
    const currentZoomNumber = getCurrentZoomNumber(
      zoom,
      fit.zoomToFitPercentage
    );
    setAvailableDimensions(zoomedSize(imageDimensions, currentZoomNumber));
    setZoomToFitPercentage(fit.zoomToFitPercentage);
  }, [modalData, zoom, setZoomToFitPercentage]);

  useEffect(() => {
    if (isOpen) {
      calculateAvailableDimensions();
      const handleResize = () => {
        calculateAvailableDimensions();
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
    return () => {};
  }, [isOpen, calculateAvailableDimensions, zoom]);

  // Register remaining non-inputSystem keyboard handlers (plain arrow navigation)
  useEffect(() => {
    if (isOpen) {
      // TODO: migrate remaining modal shortcuts to the inputSystem
      // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
      document.addEventListener('keydown', handleModalKeyDown, true);
    }
    return () => {
      document.removeEventListener('keydown', handleModalKeyDown, true);
    };
  }, [isOpen, handleModalKeyDown]);

  const tileMediaLoaderKey = useMemo(() => {
    if (!currentTileMediaSpec) {
      return null;
    }
    const key = `${currentTileMediaSpec.runSignature.entityName}-${currentTileMediaSpec.runSignature.projectName}-${currentTileMediaSpec.runSignature.runName}-${currentTileMediaSpec.mediaKey}-${currentTileMediaSpec.mediaStep}-${currentTileMediaSpec.mediaIndex}`;
    return key;
  }, [currentTileMediaSpec]);

  // The clicked card's size only describes the first image; each image loaded
  // while navigating reports its own size so the Fit box follows it.
  const handleImageRender = useCallback(
    (size: MediaSize) => {
      if (
        modalData == null ||
        (modalData.imageDimensions?.width === size.width &&
          modalData.imageDimensions?.height === size.height)
      ) {
        return;
      }
      updateModalData({...modalData, imageDimensions: size});
    },
    [modalData, updateModalData]
  );

  // avoiding passing object literals as props to TileMediaLoader
  const stableControls = useMemo(() => ({}), []);
  const stableConfig = useMemo(() => ({}), []);

  const handleClose = useCallback(() => {
    // The modal will handle applying pending changes internally
    closeModal();
  }, [closeModal]);

  const titleLinkToRun = useSingleRunLink(modalData?.runSignature);

  const imageContainerRef = useRef<HTMLDivElement>(null);

  const handleUserScrollAnalytics = useFullscreenScrollAnalytics(
    viewportCenterPercent
  );
  const handleDragEnd = useCallback((drag: DragSummary) => {
    recordRumAction('full_screen_modal_image_click_and_drag', drag);
    mediaFullscreenModalImageClickAndDrag(drag);
  }, []);
  const {
    isPanning,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onScroll: handleScroll,
  } = useScrollCenterPan({
    containerRef: imageContainerRef,
    viewportCenterPercent,
    setViewportCenterPercent,
    restoreKeys: [zoom, availableDimensions],
    onUserScroll: handleUserScrollAnalytics,
    onDragEnd: handleDragEnd,
  });

  if (!isOpen || !modalData || !currentTileMediaSpec) {
    return null;
  }

  const {labels, fullScreenTitle} = modalData;

  const isPannable =
    availableDimensions.width > containerSize.width ||
    availableDimensions.height > containerSize.height;

  return (
    <Root open={isOpen} onOpenChange={handleClose}>
      <Portal>
        {/* A blocking, full-viewport modal: the modal layer covers the legacy
            nav, which its own header would otherwise sit under. */}
        <Content
          className="z-[var(--layer-modal)] m-0 flex h-full w-full flex-col flex-1 p-0 min-w-0 max-w-full max-h-full focus:outline-none"
          data-test="media-modal-root">
          <Tailwind className="m-0 flex h-full max-h-full flex-col flex-1 p-0">
            <div
              className="media-modal-header flex flex-0 flex-col z-10"
              data-test="media-modal-header"
              // Expose modal chrome only; the media content area below stays
              // masked (and image srcs are hidden at the img level).
              data-dd-privacy={DD_PRIVACY_ALLOW}>
              <MediaBrowserModalHeader
                onClose={handleClose}
                tileMediaSpec={modalData.tileMediaSpec}
                fullScreenTitle={fullScreenTitle}
                titleLinkToRun={titleLinkToRun}
                labels={labels}
              />
            </div>

            <div
              ref={modalContentRef}
              className="flex flex-1 flex-col min-h-0 [&_img]:pointer-events-none"
              data-test="media-modal-content">
              <S.FullscreenWrapper className="fullscreen flex flex-1 items-center justify-center min-h-0 overflow-auto">
                <S.FullscreenImageContainer
                  data-test="image-scroll-container"
                  ref={imageContainerRef}
                  className={clsx(
                    'flex z-0',
                    // Align content to top-left when zoomed so we can scroll to see the whole image.
                    // Center alignment would put overflow in negative scroll space (unreachable).
                    isPannable
                      ? 'items-start justify-start'
                      : 'items-center justify-center',
                    currentTileMediaSpec.pixelated && 'media-cards--pixelated',
                    isPannable
                      ? isPanning
                        ? 'cursor-grabbing'
                        : 'cursor-pointer'
                      : 'cursor-inherit'
                  )}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onScroll={handleScroll}>
                  <TileMediaLoader
                    key={tileMediaLoaderKey}
                    tileMediaSpec={currentTileMediaSpec}
                    attemptNonexistentSteps={true}
                    width={availableDimensions?.width ?? 1000}
                    height={availableDimensions?.height ?? 800}
                    actualSize={false}
                    controls={stableControls}
                    mediaPanelRef={modalData.mediaPanelRef}
                    config={stableConfig}
                    run={currentTileMediaSpec.run}
                    runSignature={currentTileMediaSpec.runSignature}
                    labels={labels}
                    globalStep={currentTileMediaSpec.mediaStep}
                    mediaKey={currentTileMediaSpec.mediaKey}
                    mediaIndex={currentTileMediaSpec.mediaIndex}
                    omitRunLabel={true}
                    isWithinMediaBrowserModal={true}
                    onImageRender={handleImageRender}
                  />
                </S.FullscreenImageContainer>
              </S.FullscreenWrapper>
            </div>
            <div
              className="media-modal-footer flex flex-0 flex-col min-h-[0px]"
              data-test="media-modal-footer"
              data-dd-privacy={DD_PRIVACY_ALLOW}>
              <MediaBrowserModalFooter />
            </div>
          </Tailwind>
        </Content>
      </Portal>
    </Root>
  );
};

export default MediaBrowserFullscreenModal;
